import clsx from 'clsx'
import { PropertyValues, unsafeCSS } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { html } from 'lit/static-html.js'

import { DapDSContentSwitcherItem } from '@/dap-design-system'

import { DdsElement } from '../../internal/dds-hu-element'
import styles from './content-switcher.scss?inline'

/**
 * `dap-ds-content-switcher`
 * @summary A content switcher is a set of two or more segments, each of which functions as a mutually exclusive button.
 * @element dap-ds-content-switcher
 * @title - Content switcher
 *
 * @slot - The content switcher items.
 *
 * @event {{ value: string, selected: boolean }} dds-change - Fired when the content switcher is changed.
 *
 * @csspart base - The main content switcher container.
 *
 * @cssproperty --dds-content-switcher-min-width - The minimum width of the content switcher (default: var(--dds-spacing-4000, 160px))
 * @cssproperty --dds-content-switcher-padding - The padding of the content switcher (default: var(--dds-spacing-100, 4px))
 * @cssproperty --dds-content-switcher-border-width - The border width of the content switcher (default: var(--dds-border-width-base))
 * @cssproperty --dds-content-switcher-border-color - The border color of the content switcher (default: var(--dds-border-neutral-transparent))
 * @cssproperty --dds-content-switcher-border-radius - The border radius of the content switcher (default: var(--dds-radius-rounded, 1000px))
 * @cssproperty --dds-content-switcher-background - The background color of the content switcher (default: var(--dds-transparent-black-subtle, rgb(0 0 0 / 5%)))
 * @cssproperty --dds-content-switcher-gap - The gap between content switcher items (default: var(--dds-spacing-100, 4px))
 */
@customElement('dap-ds-content-switcher')
export default class DapDSContentSwitcher extends DdsElement {
  /** Whether the content switcher allows multiple selections. */
  @property({ type: Boolean, reflect: true }) multiSelect: boolean = false
  /** The value of the content switcher. A comma-separated list if `multiSelect` is `true`. */
  @property({ type: String, reflect: true }) value: string

  static readonly styles = unsafeCSS(styles)

  private getAllInputs() {
    return [
      ...this.querySelectorAll<DapDSContentSwitcherItem>(
        'dap-ds-content-switcher-item',
      ),
    ]
  }

  private async syncInputElements() {
    const inputs = this.getAllInputs()

    await Promise.all(
      inputs.map(async input => {
        if (this.multiSelect && this.value) {
          const values = this.value?.split(',').map(item => item.trim())

          input.checked = values.includes(input.value)
        } else {
          input.checked = input.value === this.value
        }
      }),
    )
  }

  private handleInputClick(event: MouseEvent) {
    const target = (
      event.target as HTMLElement
    ).closest<DapDSContentSwitcherItem>('dap-ds-content-switcher-item')
    const inputs = this.getAllInputs()
    const oldValue = this.value

    if (target!.disabled) {
      return
    }

    if (!this.multiSelect) {
      this.value = target!.value
      inputs.forEach(input => (input.checked = input === target))

      if (this.value !== oldValue) {
        this.emit('dds-change', { value: target!.value, selected: true })
      }
    } else {
      target!.checked = !target!.checked
      this.value = inputs
        .filter(input => input.checked)
        .map(input => input.value)
        .join(',')

      this.emit('dds-change', { value: this.value })
    }
  }

  protected updated(_changedProperties: PropertyValues): void {
    if (_changedProperties.has('value')) {
      this.syncInputElements()
    }
  }

  render() {
    return html`
      <div part="base" class=${clsx('content-switcher')}>
        <slot
          @click=${this.handleInputClick}
          @slotchange=${this.syncInputElements}></slot>
      </div>
    `
  }
}
